import React from 'react';
import styled from 'styled-components';
import { TypographyComponent as TypographyElement, gridConfig } from '@tvg/fuse-design';
import VisiblyHidden from '../VisiblyHidden';
import { PriceProps } from './PriceProps';

const Container = styled.div`
  margin-bottom: var(--spacing-space16);
  order: 0;

  @media (min-width: ${gridConfig.m.minWidth}) {
    order: 1;
  }
`;

const CurrentPrice = styled.p`
  font-weight: bold;
  font-size: var(--font-size-3);
  color: ${({ color }) => color || 'var(--color-global-black)'};
`;

const PreviousPrice = styled(CurrentPrice)`
  text-decoration: line-through;
  font-size: var(--font-size-1);
`;

function Price({ price }: { price: PriceProps }): JSX.Element {
  const { current, previous, saving, currentFrom, previousFrom, savingTo, unitPrice } = price;

  const renderPrevious = (): JSX.Element => {
    const text = previousFrom ? `From ${previousFrom}` : previous;
    const testId = `product_price_${previousFrom ? 'previousFrom' : 'previous'}`;
    const accessibilityText = `Previous price ${text?.toLowerCase()}`;

    return (
      <>
        <PreviousPrice data-testid={testId} aria-hidden>
          {text}
        </PreviousPrice>
        <VisiblyHidden text={accessibilityText} testId={`${testId}_accessibilityText`} />
      </>
    );
  };

  const getCurrentPriceText = (): string => {
    let result = '';

    if (currentFrom) {
      result += `From ${currentFrom}`;
    } else {
      result += current;
    }

    if (savingTo) {
      result += ` (Save up to ${savingTo})`;
    } else if (saving) {
      result += ` (Save ${saving})`;
    }

    return result;
  };

  const renderPriceColour = () => (!!saving || !!savingTo ? 'var(--color-universal-redMid)' : undefined);

  const renderCurrent = (): JSX.Element => {
    const text = getCurrentPriceText();
    const testId = `product_price_${currentFrom ? 'currentFrom' : 'current'}`;
    const accessibilityText = `Current price ${text.toLowerCase()}`;

    return (
      <>
        <CurrentPrice color={renderPriceColour()} data-testid={testId} aria-hidden>
          {text}
        </CurrentPrice>
        {unitPrice && (
          <TypographyElement data-testid="unit-price" type="paragraph" size="1">
            {unitPrice}
          </TypographyElement>
        )}
        <VisiblyHidden text={accessibilityText} testId={`${testId}_accessibilityText`} />
      </>
    );
  };

  return (
    <Container>
      {(previousFrom || previous) && renderPrevious()}
      {renderCurrent()}
    </Container>
  );
}

export default Price;
